import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { environment } from "../../environments/environment";
import { Router } from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import { IPrincipalService } from "@vision/viewer-components";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService implements IPrincipalService {
  private currentUserSubject: BehaviorSubject<any>;

  public currentUser: Observable<any>;

  private currentEmailSubject: BehaviorSubject<any>;

  public currentEmail: Observable<any>;

  constructor(
    private http: HttpClient,
    private router: Router,
    private jwtHelper: JwtHelperService,
  ) {
    this.currentUserSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("currentUser")),
    );
    this.currentUser = this.currentUserSubject.asObservable();
    this.currentEmailSubject = new BehaviorSubject<any>("");
    this.currentEmail = this.currentEmailSubject.asObservable();
  }

  public getQueryAuth(): string {
    return `${this.getAuthParam()}=${this.getAccessToken()}`;
  }

  public getAuthParam(): string {
    switch (this.getAuthType()) {
      case "api_key":
      case "impersonation_key":
      case "geo_key":
        return this.getAuthType();
      default:
        return "token";
    }
  }

  public getAuthType(): string {
    return this.currentUserValue.type;
  }

  public get currentUserValue() {
    return this.currentUserSubject.value;
  }

  public set currentUserValue(user) {
    this.currentUserSubject.next(user);
  }

  public getPrincipal(): string | null {
    return this.currentUserValue?.name ?? null;
  }

  public setCurrentUser(user: Record<string, unknown>): void {
    localStorage.setItem("currentUser", JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  public clearCurrentUser(): void {
    this.cleanAuthInfo();
  }

  // region Services calls

  login(username, password) {
    return this.http
      .post<any>(`${environment.viewerUrl}/api/auth/signin`, {
        username,
        password,
      })
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  authenticate(keyValue, keyParam) {
    return this.http
      .post<any>(
        `${environment.viewerUrl}/api/auth/authenticate?${keyParam}=${keyValue}`,
        {},
      )
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  register(company, email, name, data) {
    return this.http
      .post<any>(`${environment.viewerUrl}/api/auth/${company}/signup`, {
        email,
        name,
        data,
      })
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  logout() {
    // remove user from local storage and set current user to null
    localStorage.removeItem("currentUser");
    this.currentUserSubject.next(null);
    return of();
  }

  activate(key, username, password, name) {
    const body = {
      key,
      username,
      password,
      name,
    };
    const options = {
      headers: {
        "content-Type": "application/json",
      },
    };
    return this.http
      .post<any>(`${environment.viewerUrl}/api/auth/activate`, body, options)
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  getAuthSystemForUser(username) {
    const body = {
      username,
    };
    const options = {
      headers: {
        "content-Type": "application/json",
      },
    };
    return this.http
      .post<any>(`${environment.viewerUrl}/api/auth/check/login`, body, options)
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  getAuthSystemForDomain(domain) {
    const body = {
      domain,
    };
    const options = {
      headers: {
        "content-Type": "application/json",
      },
    };
    return this.http
      .post<any>(`${environment.viewerUrl}/api/auth/check/login`, body, options)
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  federatedAuthLogin(code, state) {
    const body = {
      code,
      state,
      redirect: `${environment.viewerUrl}/api/auth/federatedAuth/login`,
    };
    const options = {
      headers: {
        "content-Type": "application/json",
      },
    };
    return this.http
      .post<any>(
        `${environment.viewerUrl}/api/auth/federatedAuth/login`,
        body,
        options,
      )
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  sendResetLink(email) {
    const body = {
      key: btoa(email),
      email,
    };
    const options = {
      headers: {
        "content-Type": "application/json",
      },
    };
    return this.http
      .post<any>(
        `${environment.viewerUrl}/api/auth/password/forgot`,
        body,
        options,
      )
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  checkResetPassword(key) {
    const body = {
      key,
    };
    const options = {
      headers: {
        "content-Type": "application/json",
      },
    };
    return this.http
      .post<any>(
        `${environment.viewerUrl}/api/auth/password/check`,
        body,
        options,
      )
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  checkUserActive(key) {
    const body = {
      key,
    };
    const options = {
      headers: {
        "content-Type": "application/json",
      },
    };
    return this.http
      .post<any>(
        `${environment.viewerUrl}/api/auth/activate/check`,
        body,
        options,
      )
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  resetPassword(key, password) {
    const body = {
      key,
      password,
    };
    const options = {
      headers: {
        "content-Type": "application/json",
      },
    };
    return this.http
      .post<any>(
        `${environment.viewerUrl}/api/auth/password/reset`,
        body,
        options,
      )
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  changePassword(key, password) {
    const body = {
      key,
      password,
    };
    const options = {
      headers: {
        "content-Type": "application/json",
      },
    };
    return this.http
      .post<any>(
        `${
          environment.viewerUrl
        }/api/auth/password/change?${this.getQueryAuth()}`,
        body,
        options,
      )
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }

  // endregion

  getAccessToken() {
    return this.currentUserSubject.getValue().token;
  }

  countLoggedTimes() {
    let loggedTimes = localStorage.getItem("loggedTimes")
      ? JSON.parse(localStorage.getItem("loggedTimes"))
      : 0;
    loggedTimes += 1;
    localStorage.setItem("loggedTimes", JSON.stringify(loggedTimes));
  }

  cleanAuthInfo() {
    localStorage.removeItem("currentUser");
    this.currentUserSubject.next(null);
  }

  public set currentEmailValue(email) {
    this.currentEmailSubject.next(email);
  }

  private redirectFederateAuth() {
    this.currentUserValue.refreshSession = true;
    const queryParams = { reloadUser: true };
    this.router.navigate(["sign/login/federated"], { queryParams });
  }

  prepareFederateRedirectUrl(
    redirectUrl: string,
    username: string,
    origin: string,
    lat: string,
    lon: string,
    zoom: string,
    address: string,
    state1: string,
    state2: string,
  ) {
    const urlId = uuidv4();
    const urlStatus = this.encodeString(
      `${urlId}##${lat}##${lon}##${zoom}##${address}##${state1}##${state2}`,
    );
    const redirectURI = this.encodeString(redirectUrl);

    localStorage.setItem("redirectURI", redirectURI);
    localStorage.setItem(`urlStatus#${urlId}`, urlStatus);

    const tempUrl = new URL(redirectUrl);
    tempUrl.searchParams.set(
      "redirect_uri",
      `${environment.viewerUrl}/api/auth/federatedAuth/login`,
    );

    //Generate custom state and add the one from the check service
    const state = this.encodeString(
      `${username}##${window.location.origin}/#/sign/login/federated##${urlId}##${tempUrl.searchParams.get("state")}`,
    );

    tempUrl.searchParams.set("state", state);
    return tempUrl.href;
  }

  encodeString(value: string): string {
    return window.btoa(unescape(encodeURIComponent(value)));
  }

  decodeParameters(value: string): string[] {
    return window.atob(value).split("##");
  }

  isTokenExpired() {
    const currentUser = this.currentUserValue;
    if (currentUser && this.isCurrentUserKey()) {
      return false;
    } else if (currentUser) {
      const token = currentUser.token;

      if (token) {
        try {
          return this.jwtHelper.isTokenExpired(token);
        } catch (error) {
          return true;
        }
      }
    } else {
      return true;
    }
  }

  redirectToLogin(queryParams: any, stateUrl: string) {
    if (this.isCurrentUserFederated()) {
      this.redirectFederateAuth();
    } else {
      localStorage.removeItem("currentUser");
      this.currentUserValue = null;
      this.router.navigate(["/sign/login"], {
        queryParams: {
          ...queryParams,
          returnUrl: stateUrl,
        },
      });
    }
  }

  isCurrentUserFederated() {
    return (
      !!this.currentUserValue &&
      this.currentUserValue.login_type === "federated"
    );
  }

  isCurrentUserNonFederated() {
    return (
      !!this.currentUserValue &&
      this.currentUserValue.login_type === "non-federated"
    );
  }

  isCurrentUserKey() {
    return !(this.isCurrentUserFederated() || this.isCurrentUserNonFederated());
  }
}
